import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardLayout, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import SignatureCanvas from "react-signature-canvas";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  FileSignature,
  CheckCircle,
  Shield,
  AlertTriangle,
  Loader2,
  FileText,
  User,
  Eraser
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamSignContract() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [cedePercentage, setCedePercentage] = useState(0);
  const [hazardChecks, setHazardChecks] = useState({});
  const [ppeChecks, setPpeChecks] = useState({});
  const signaturePadRef = useRef(null);

  // PPE emoji mapping
  const ppeEmojis = {
    helmet: "⛑️",
    safety_shoes: "👢",
    hi_vis: "🦺",
    harness: "🪢",
    gloves: "🧤",
    ear_protection: "🎧",
    safety_glasses: "🥽",
    dust_mask: "😷",
    face_shield: "🛡️",
    knee_pads: "🦵",
    respirator: "😮‍💨",
    safety_harness: "🪢"
  };

  // Hazard label mapping
  const hazardLabels = {
    fall_height: "Fall fra høyde",
    falling_objects: "Fallende gjenstander",
    electrical: "Elektrisk fare",
    chemical: "Kjemisk fare",
    noise: "Støy",
    dust: "Støv",
    sharp_edges: "Skarpe kanter",
    heavy_lifting: "Tunge løft",
    confined_space: "Trang plass",
    weather: "Værforhold"
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const isSignatureEmpty = () => {
    return signaturePadRef.current?.isEmpty() ?? true;
  };

  // Check if all SJA items are checked
  const allSjaChecked = () => {
    const sja = data?.project?.sja_form;
    if (!sja) return true;
    
    const hazards = sja.hazards || [];
    const customHazards = sja.custom_hazards || [];
    const ppeRequired = sja.ppe_required || [];
    
    const allHazardsChecked = hazards.every((_, idx) => hazardChecks[`hazard_${idx}`]);
    const allCustomHazardsChecked = customHazards.every((_, idx) => hazardChecks[`custom_${idx}`]);
    const allPpeChecked = ppeRequired.every((ppe) => ppeChecks[ppe]);
    
    return allHazardsChecked && allCustomHazardsChecked && allPpeChecked;
  };

  useEffect(() => {
    fetchContractData();
  }, [projectId, token]);

  const fetchContractData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects-v3/team-contract/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      if (user?.name) {
        setSignerName(user.name);
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
      toast.error("Could not load contract");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!allSjaChecked()) {
      toast.error(t('contracts.checkAllSja') || 'Please check all hazards and PPE items before signing');
      return;
    }
    if (!agreedToTerms) {
      toast.error(t('contracts.mustAgreeToTerms'));
      return;
    }
    if (!signerName.trim()) {
      toast.error(t('contracts.enterYourName'));
      return;
    }
    if (isSignatureEmpty()) {
      toast.error(t('contracts.pleaseSign') || 'Please draw your signature');
      return;
    }

    // Get signature as base64 image
    const signatureData = signaturePadRef.current?.toDataURL('image/png');

    setSigning(true);
    try {
      const userRole = data?.user_role;
      
      if (userRole === "team_leader") {
        // TL signs
        await axios.post(
          `${API_URL}/api/projects-v3/team-sign-contract/${projectId}`,
          { 
            signer_name: signerName,
            signature_image: signatureData
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('contracts.contractSigned'));
      } else {
        // Worker signs with optional cede percentage
        await axios.post(
          `${API_URL}/api/projects-v3/worker-sign-contract/${projectId}`,
          { 
            signer_name: signerName,
            cede_percentage: cedePercentage,
            signature_image: signatureData
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('contracts.contractSigned'));
      }
      
      // Refresh data
      await fetchContractData();
    } catch (error) {
      console.error("Error signing:", error);
      toast.error(error.response?.data?.detail || "Could not sign contract");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={teamLeaderNavItems} title="Sign Contract">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data?.needs_signature) {
    return (
      <DashboardLayout navItems={teamLeaderNavItems} title="Contract">
        <div className="max-w-2xl mx-auto text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Contract Already Signed</h2>
          <p className="text-gray-400 mb-6">This contract has already been signed.</p>
          <Button onClick={() => navigate("/team-leader/my-projects")} className="bg-[#23F7DD] text-black">
            Go to My Projects
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { project, application, fee_breakdown } = data;
  
  // Calculate duration from dates if not set
  const calculateDuration = () => {
    if (project?.duration_days) return `${project.duration_days} days`;
    if (project?.start_date && project?.end_date) {
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      return `${days} days`;
    }
    return "TBD";
  };

  return (
    <DashboardLayout navItems={teamLeaderNavItems} title="Sign Team Contract">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
              Sign Team Contract
            </h1>
            <p className="text-gray-400 mt-1">
              Review and sign the contract for {project?.title}
            </p>
          </div>
        </div>

        {/* Project Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#23F7DD]" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-gray-500 text-sm">Project</p>
                <p className="text-white font-semibold">{project?.title}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Location</p>
                <p className="text-white flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-[#23F7DD]" />
                  {project?.location_city}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Start Date</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-green-400" />
                  {project?.start_date ? new Date(project.start_date).toLocaleDateString('nb-NO') : "TBD"}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Delivery Deadline</p>
                <p className="text-white flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-red-400" />
                  {project?.end_date ? new Date(project.end_date).toLocaleDateString('nb-NO') : "TBD"}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-gray-500 text-sm">Duration</p>
                <p className="text-white font-semibold">{calculateDuration()}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Project Type</p>
                <p className="text-white capitalize">{project?.project_type?.replace("_", " ") || "Stillastjenester"}</p>
              </div>
            </div>
            
            {/* Project Description */}
            {project?.description && (
              <div className="mt-4 p-4 bg-white/5 rounded-lg">
                <p className="text-gray-500 text-sm mb-2">Project Description</p>
                <p className="text-gray-300">{project.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SJA - Safety Analysis */}
        {project?.sja_form && (
          <Card className="bg-[#0A0A0A] border-orange-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-orange-400" />
                Safety Analysis (SJA)
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Important</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Job Description */}
              {project.sja_form.job_description && (
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-gray-500 text-sm mb-1">Job Description</p>
                  <p className="text-white">{project.sja_form.job_description}</p>
                </div>
              )}
              
              {/* Emergency Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Emergency Contact */}
                {project.sja_form.emergency_contact && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">📞 Emergency Contact</p>
                    <p className="text-white font-bold text-lg">{project.sja_form.emergency_contact}</p>
                  </div>
                )}
                
                {/* Nearest Hospital */}
                {project.sja_form.nearest_hospital && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm mb-1">🏥 Nearest Hospital</p>
                    <p className="text-white font-semibold">{project.sja_form.nearest_hospital}</p>
                  </div>
                )}
                
                {/* First Aid Location */}
                {project.sja_form.first_aid_location && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 text-sm mb-1">🩹 First Aid Location</p>
                    <p className="text-white font-semibold">{project.sja_form.first_aid_location}</p>
                  </div>
                )}
              </div>
              
              {/* Hazards with Checkboxes */}
              {project.sja_form.hazards && project.sja_form.hazards.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    ⚠️ Identified Hazards - Check each to confirm you understand
                  </p>
                  <div className="space-y-3">
                    {project.sja_form.hazards.map((hazard, idx) => {
                      const hazardId = typeof hazard === 'string' ? hazard : (hazard.id || `hazard_${idx}`);
                      const hazardLabel = hazardLabels[hazardId] || hazardId.replace(/_/g, ' ');
                      const hazardMeasures = typeof hazard === 'object' ? hazard.measures : null;
                      const hazardResponsible = typeof hazard === 'object' ? hazard.responsible : null;
                      const hazardProbability = typeof hazard === 'object' ? hazard.probability : null;
                      const hazardConsequence = typeof hazard === 'object' ? hazard.consequence : null;
                      const isChecked = hazardChecks[`hazard_${idx}`];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border transition-all ${
                            isChecked 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-red-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`hazard_${idx}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => setHazardChecks(prev => ({...prev, [`hazard_${idx}`]: checked}))}
                              className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <div className="flex-1">
                              <label htmlFor={`hazard_${idx}`} className="text-white font-semibold cursor-pointer capitalize flex items-center gap-2">
                                ⚠️ {hazardLabel}
                                {isChecked && <CheckCircle className="w-4 h-4 text-green-400" />}
                              </label>
                              
                              {(hazardProbability || hazardConsequence) && (
                                <div className="flex gap-3 mt-1 text-xs">
                                  {hazardProbability && (
                                    <span className="text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded">
                                      📊 Risk: {hazardProbability}
                                    </span>
                                  )}
                                  {hazardConsequence && (
                                    <span className="text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">
                                      💥 Impact: {hazardConsequence}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {hazardMeasures && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                                  <p className="text-sm text-green-400 font-medium">✅ Prevention Measures:</p>
                                  <p className="text-gray-300 text-sm">{hazardMeasures}</p>
                                </div>
                              )}
                              
                              {hazardResponsible && (
                                <p className="text-sm text-purple-400 mt-1">
                                  👤 Responsible: {hazardResponsible}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Custom Hazards with Checkboxes */}
              {project.sja_form.custom_hazards && project.sja_form.custom_hazards.length > 0 && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    🔶 Additional Hazards
                  </p>
                  <div className="space-y-3">
                    {project.sja_form.custom_hazards.map((hazard, idx) => {
                      const isChecked = hazardChecks[`custom_${idx}`];
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border transition-all ${
                            isChecked 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-orange-500/30'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`custom_${idx}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => setHazardChecks(prev => ({...prev, [`custom_${idx}`]: checked}))}
                              className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <div className="flex-1">
                              <label htmlFor={`custom_${idx}`} className="text-white font-semibold cursor-pointer flex items-center gap-2">
                                🔶 {hazard.description || `Custom Hazard ${idx + 1}`}
                                {isChecked && <CheckCircle className="w-4 h-4 text-green-400" />}
                              </label>
                              
                              {(hazard.probability || hazard.consequence) && (
                                <div className="flex gap-3 mt-1 text-xs">
                                  {hazard.probability && (
                                    <span className="text-yellow-400 bg-yellow-500/20 px-2 py-0.5 rounded">
                                      📊 Risk: {hazard.probability}
                                    </span>
                                  )}
                                  {hazard.consequence && (
                                    <span className="text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">
                                      💥 Impact: {hazard.consequence}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {hazard.measures && (
                                <div className="mt-2 p-2 bg-green-500/10 rounded border-l-2 border-green-500">
                                  <p className="text-sm text-green-400 font-medium">✅ Prevention Measures:</p>
                                  <p className="text-gray-300 text-sm">{hazard.measures}</p>
                                </div>
                              )}
                              
                              {hazard.responsible && (
                                <p className="text-sm text-purple-400 mt-1">
                                  👤 Responsible: {hazard.responsible}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* PPE Required with Checkboxes and Emojis */}
              {project.sja_form.ppe_required && project.sja_form.ppe_required.length > 0 && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    🛡️ Required PPE - Check each to confirm you have it
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {project.sja_form.ppe_required.map((ppe, idx) => {
                      const isChecked = ppeChecks[ppe];
                      const emoji = ppeEmojis[ppe] || "🔧";
                      const ppeLabel = ppe.replace(/_/g, ' ');
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border transition-all cursor-pointer ${
                            isChecked 
                              ? 'bg-green-500/20 border-green-500/50' 
                              : 'bg-white/5 border-blue-500/30 hover:border-blue-500/50'
                          }`}
                          onClick={() => setPpeChecks(prev => ({...prev, [ppe]: !prev[ppe]}))}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={(checked) => setPpeChecks(prev => ({...prev, [ppe]: checked}))}
                              className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <span className="text-2xl">{emoji}</span>
                            <span className={`text-sm capitalize ${isChecked ? 'text-green-400' : 'text-white'}`}>
                              {ppeLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Additional Safety Measures */}
              {project.sja_form.additional_measures && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm mb-1">📋 Additional Safety Measures</p>
                  <p className="text-gray-300">{project.sja_form.additional_measures}</p>
                </div>
              )}
              
              {/* SJA Completion Status */}
              <div className={`p-3 rounded-lg border ${
                allSjaChecked() 
                  ? 'bg-green-500/20 border-green-500/50' 
                  : 'bg-yellow-500/10 border-yellow-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  {allSjaChecked() ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-semibold">✅ All safety items confirmed</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-semibold">⚠️ Please check all hazards and PPE items above before signing</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Team Info with Shares */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-[#23F7DD]" />
              {t('projectDetail.teamLeader')} & Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {/* Show member shares from API */}
              {data?.member_shares?.map((member, idx) => {
                const isCurrentUser = member.user_id === user?.user_id;
                const isTL = member.role === "Team Leader";
                
                return (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isTL 
                        ? 'bg-purple-500/10 border-purple-500/30' 
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isTL ? 'bg-purple-500/20' : 'bg-blue-500/20'
                      }`}>
                        <User className={`w-5 h-5 ${isTL ? 'text-purple-400' : 'text-blue-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold">{member.name}</p>
                          {member.worker_code && (
                            <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                              {member.worker_code}
                            </Badge>
                          )}
                          {isCurrentUser && (
                            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] text-xs">
                              {t('common.you')}
                            </Badge>
                          )}
                        </div>
                        <p className={`text-sm ${isTL ? 'text-purple-400' : 'text-blue-400'}`}>
                          {member.role}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Share amount */}
                      <div className="text-right">
                        <p className="text-white font-bold text-lg">
                          {member.final_share?.toLocaleString()} kr
                        </p>
                        {isTL && member.ceded_from_team > 0 && (
                          <p className="text-xs text-green-400">
                            +{member.ceded_from_team?.toLocaleString()} kr fra team
                          </p>
                        )}
                        {!isTL && member.cede_percentage > 0 && isCurrentUser && (
                          <p className="text-xs text-yellow-400">
                            -{member.cede_percentage}% til TL
                          </p>
                        )}
                      </div>
                      
                      {/* Signature status */}
                      <div className="flex flex-col items-center">
                        {member.has_signed ? (
                          <>
                            <CheckCircle className="w-6 h-6 text-green-400" />
                            <span className="text-xs text-green-400">{t('contracts.signed')}</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-6 h-6 text-yellow-400" />
                            <span className="text-xs text-yellow-400">{t('contracts.unsigned')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* If no member_shares data yet, show basic info */}
              {!data?.member_shares?.length && (
                <>
                  <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{application?.team_leader_name || user?.name}</p>
                      <p className="text-purple-400 text-sm">Team Leader</p>
                    </div>
                  </div>
                  
                  {application?.team_members?.map((member, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white">{member.name}</p>
                        <p className="text-blue-400 text-sm">{member.role}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Financial Details - Only show net_to_team and share info (team_budget is for Holding only) */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#23F7DD]" />
              {t('contracts.financialTerms') || 'Financial Terms'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.netToTeam')}</p>
                  <p className="text-sm text-gray-500 mb-1">({data?.member_shares?.length || (1 + (application?.team_members?.length || 0))} {t('common.members') || 'medlemmer'})</p>
                  <p className="text-2xl font-bold text-green-400">
                    {(project?.net_to_team || fee_breakdown?.net_to_team)?.toLocaleString()} kr
                  </p>
                </div>
                <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-gray-400 text-sm">{t('contracts.yourBaseShare') || 'Din grunnandel'}</p>
                  <p className="text-sm text-gray-500 mb-1">({t('common.beforeTax') || 'før skatt'})</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {data?.share_per_member?.toLocaleString() || Math.round((project?.net_to_team || 0) / Math.max(1 + (application?.team_members?.length || 0), 1)).toLocaleString()} kr
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {t('projectDetail.paymentNotice') || 'Betaling skjer etter fullført prosjekt'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#23F7DD]" />
              Contract Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <p>By signing this contract, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Complete the scaffolding work as specified in the project requirements</li>
                <li>Follow all safety regulations and AllStillas guidelines</li>
                <li>Provide daily progress updates during the project</li>
                <li>Use the team members listed in your application</li>
                <li>Complete the work within the specified timeline</li>
                <li>Accept the financial terms as outlined above</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        {data?.user_needs_to_sign && (
          <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-[#23F7DD]" />
                {t('contracts.signContract')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Name Input */}
              <div className="space-y-2">
                <Label htmlFor="signerName" className="text-white">{t('contracts.fullNameForSignature')}</Label>
                <Input
                  id="signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder={t('contracts.enterFullName')}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>

              {/* Signature Pad */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-white">{t('contracts.yourSignature')}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSignature}
                    className="border-white/20 text-gray-400 hover:text-white hover:bg-white/10"
                  >
                    <Eraser className="w-4 h-4 mr-1" />
                    {t('common.clear')}
                  </Button>
                </div>
                <div className="border-2 border-dashed border-white/30 rounded-lg bg-white overflow-hidden">
                  <SignatureCanvas
                    ref={signaturePadRef}
                    canvasProps={{
                      className: "w-full h-32",
                      style: { width: '100%', height: '150px' }
                    }}
                    penColor="black"
                    backgroundColor="white"
                  />
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {t('contracts.drawSignature') || 'Draw your signature above using mouse or touch'}
                </p>
              </div>

              {/* Worker Cede Percentage Option - Only for workers */}
              {data?.user_role === "worker" && (
                <div className="space-y-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <div>
                    <p className="text-white font-semibold">{t('contracts.cedeToTL') || 'Cede % to Team Leader'}</p>
                    <p className="text-gray-400 text-sm">{t('contracts.cedeToTLDesc') || 'Optionally give a percentage of your share to the Team Leader'}</p>
                  </div>
                  
                  {/* Slider */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-purple-400 font-medium">{t('contracts.cede') || 'Cede'}: {cedePercentage}%</span>
                      <span className="text-gray-500">Max: 3%</span>
                    </div>
                    <Slider
                      value={[cedePercentage]}
                      onValueChange={(vals) => setCedePercentage(vals[0])}
                      max={3}
                      min={0}
                      step={0.5}
                      className="w-full"
                    />
                    
                    {data?.share_per_member && (
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <p className="text-sm text-gray-400">
                          {t('contracts.yourFinalShare') || 'Your final share'}:
                        </p>
                        <p className="text-xl font-bold text-[#23F7DD]">
                          {(data.share_per_member * (1 - cedePercentage / 100)).toLocaleString()} kr
                        </p>
                        {cedePercentage > 0 && (
                          <p className="text-xs text-purple-400 mt-1">
                            (-{(data.share_per_member * cedePercentage / 100).toLocaleString()} kr → TL)
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    {t('contracts.cedePrivacyNote') || 'This is private - the TL will only see the total amount ceded by the team, not who ceded.'}
                  </p>
                </div>
              )}

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1 data-[state=checked]:bg-[#23F7DD] data-[state=checked]:border-[#23F7DD]"
                />
                <label htmlFor="terms" className="text-gray-300 cursor-pointer">
                  {t('contracts.agreeToTerms')}
                </label>
              </div>

              {/* Sign Button */}
              <Button
                onClick={handleSign}
                disabled={signing || !agreedToTerms || !signerName.trim()}
                className="w-full bg-[#23F7DD] hover:bg-[#1de9cf] text-black font-bold py-6 text-lg"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('contracts.signingContract')}
                  </>
                ) : (
                  <>
                    <FileSignature className="w-5 h-5 mr-2" />
                    {t('contracts.signContract')}
                  </>
                )}
              </Button>

              <p className="text-center text-gray-500 text-sm">
                <Shield className="w-4 h-4 inline mr-1" />
                {t('contracts.electronicSignature')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Already Signed Message */}
        {!data?.user_needs_to_sign && data?.contract && (
          <Card className="bg-[#0A0A0A] border-green-500/30">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-3 text-green-400">
                <CheckCircle className="w-8 h-8" />
                <div>
                  <p className="text-lg font-semibold">{t('contracts.contractSigned')}</p>
                  <p className="text-sm text-gray-400">
                    {data.contract.team_signed_at && new Date(data.contract.team_signed_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
